import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import localeData from "dayjs/plugin/localeData";
import timezone from "dayjs/plugin/timezone"; // timezone 플러그인 추가
import utc from "dayjs/plugin/utc"; // timezone 사용하려면 utc도 필요함
import "dayjs/locale/ko"; // 한국어 로케일

// 플러그인 활성화
dayjs.extend(relativeTime);
dayjs.extend(localeData);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("ko");

type DateFormat =
  | "YYYY-MM-DD"
  | "YYYY년 MM월 DD일"
  | "YYYY.MM.DD"
  | "HH:mm:ss"
  | "YYYY-MM-DD HH:mm:ss"
  | string;

export class DateUtils {
  private static today = dayjs().tz("Asia/Seoul");

  // 시간 + 날짜
  static parseKoreanDate(date: string | Date, locale = "en") {
    return dayjs(date).tz("Asia/Seoul").locale(locale);
  }

  // 날짜만 비교
  static parseKoreanDateDay(date: string | Date) {
    return this.parseKoreanDate(date).startOf("day");
  }

  static dateFormatKR(date: string | Date, format: DateFormat) {
    return this.parseKoreanDate(date).format(format);
  }

  static isAfter(date: string): boolean {
    return this.today.isAfter(this.parseKoreanDateDay(date), "day");
  }

  static formatStyledShort(date: string | Date) {
    return this.parseKoreanDate(date).format("MMM D, YYYY");
  }

  static isBefore(date: string) {
    return this.today.isBefore(this.parseKoreanDateDay(date), "day");
  }

  static isSame(date: string) {
    return this.today.isSame(this.parseKoreanDateDay(date), "day");
  }

  static isNew(date: string | Date) {
    const diffDays = this.today.diff(this.parseKoreanDateDay(date), "day");
    return diffDays >= 0 && diffDays < 7;
  }

  static isToday(date: string | Date) {
    const diffDays = this.today.diff(this.parseKoreanDateDay(date), "day");
    return diffDays === 0;
  }

  static fromNow(date: string) {
    return this.parseKoreanDate(date, "kr").fromNow();
  }

  /**
   * 두 날짜 사이의 일수 차이를 계산합니다. (종료일 - 시작일)
   * @param startDate 시작 날짜
   * @param endDate 종료 날짜
   * @returns 두 날짜 사이의 일수 (종료일 포함)
   */
  static getDurationDays(
    startDate: string | Date,
    endDate: string | Date
  ): number {
    const start = this.parseKoreanDate(startDate);
    const end = this.parseKoreanDate(endDate);

    // 종료일도 포함하는 경우 +1 (예: 5월 1일부터 5월 3일까지는 3일)
    return end.diff(start, "day") + 1;
  }

  /**
   * 두 날짜 사이의 기간을 '00일' 형식의 문자열로 반환합니다.
   * @param startDate 시작 날짜
   * @param endDate 종료 날짜
   * @returns '00일' 형식의 문자열
   */
  static getFormattedDuration(
    startDate: string | Date,
    endDate: string | Date
  ): string {
    const days = this.getDurationDays(startDate, endDate);
    return `${days}일`;
  }

  /**
   * 재직 기간을 'X년 X개월' 형식으로 반환합니다.
   * 1개월 미만인 경우 '1개월 미만'을 반환합니다.
   * endDate가 없으면 오늘을 기준으로 계산합니다.
   */
  static getTenureString(
    startDate: string | Date | null | undefined,
    endDate: string | Date | null | undefined
  ): string {
    if (!startDate) return "";

    const start = this.parseKoreanDate(startDate);
    const end = endDate ? this.parseKoreanDate(endDate) : this.today;

    if (!start.isValid()) return "";

    const totalMonths = end.diff(start, "month");

    if (totalMonths < 1) {
      return "1개월 미만";
    }

    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;

    if (years > 0) {
      return months === 0 ? `${years}년` : `${years}년 ${months}개월`;
    }
    return `${months}개월`;
  }

  /**
   * 초(seconds) 단위 시간을 오전/오후 HH:mm 형식으로 변환
   * @param seconds - 자정(00:00)부터의 경과 시간(초)
   * @returns "오전/오후 HH:mm" 형식 문자열
   * @example
   * DateUtils.secondsToTime(54000) // "오후 03:00"
   * DateUtils.secondsToTime(21600) // "오전 06:00"
   */
  static secondsToTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    const period = hours < 12 ? "오전" : "오후";
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

    return `${period} ${String(displayHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  }

  /**
   * "HH:mm" 형식의 시간 문자열을 "오전/오후 hh:mm" 형식으로 변환합니다.
   * "24:00"은 "오전 12:00"으로 처리합니다.
   * @param timeString - "HH:mm" 형식의 시간 문자열
   * @returns "오전/오후 hh:mm" 형식 문자열 또는 유효하지 않은 경우 '--:--'
   */
  static formatTimeString(timeString: string | null): string {
    if (!timeString || !timeString.includes(":")) {
      return "--:--";
    }

    let adjustedTimeString = timeString;
    if (timeString === "24:00") {
      adjustedTimeString = "00:00";
    }

    const d = dayjs(`2000-01-01T${adjustedTimeString}`);
    if (!d.isValid()) {
      return "--:--";
    }

    return d.format("A hh:mm");
  }

  /**
   * 근무 시간대를 포맷팅
   * @param startTime - 근무 시작 시간(초)
   * @param endTime - 근무 종료 시간(초)
   * @returns "오전/오후 HH:mm~오전/오후 HH:mm" 형식
   * @example
   * DateUtils.formatShiftTime(32400, 64800) // "오전 09:00~오후 06:00"
   * DateUtils.formatShiftTime(79200, 21600) // "오후 10:00~오전 06:00"
   */
  static formatShiftTime(startTime: number, endTime: number): string {
    // 시작과 끝이 모두 0이면 시간 정보 없음
    if (startTime === 0 && endTime === 0) {
      return "--:--";
    }
    const start = this.secondsToTime(startTime);
    const end = this.secondsToTime(endTime);

    return `${start}~${end}`;
  }
}

export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0: 월요일 ~ 6: 일요일

export const WEEKDAY_LABELS = [
  "월",
  "화",
  "수",
  "목",
  "금",
  "토",
  "일",
] as const;
export const FULL_WEEKDAY_LABELS = [
  "월요일",
  "화요일",
  "수요일",
  "목요일",
  "금요일",
  "토요일",
  "일요일",
] as const;
export const WEEKDAY_SHORT_SUN_FIRST = [
  "일",
  "월",
  "화",
  "수",
  "목",
  "금",
  "토",
] as const;

export const getWeekdayLabel = (weekday: Weekday): string => {
  return WEEKDAY_LABELS[weekday];
};

export const getFullWeekdayLabel = (weekday: Weekday): string => {
  return FULL_WEEKDAY_LABELS[weekday];
};

/**
 * 특정 년월의 마지막 날짜를 반환
 * @param year - 년도
 * @param month - 월 (1-12)
 * @returns 해당 월의 마지막 날짜
 * @example
 * getDaysInMonth(2025, 12) // 31
 */
export const getDaysInMonth = (year: number, month: number): number => {
  return new Date(year, month, 0).getDate();
};

/**
 * ISO 날짜 문자열 또는 Date 객체를 "YYMMDD-HH:mm" 형식으로 변환
 * @param date - ISO 형식 날짜 문자열 또는 Date 객체
 * @returns "YYMMDD-HH:mm" 형식 문자열
 * @example
 * formatToCustomDate("2025-12-16T10:43:05") // "251216-10:43"
 * formatToCustomDate(new Date()) // "251216-10:43"
 */
export const formatToCustomDate = (date: string | Date): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;

  const year = String(dateObj.getFullYear()).slice(-2);
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  const hours = String(dateObj.getHours()).padStart(2, "0");
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");

  return `${year}${month}${day}-${hours}:${minutes}`;
};
